import { RevisionGetResponse as Revision } from 'api/actions/revision-get/revision-get-response';
import { useApi } from 'api/api-context';
import { RevisionFormData } from 'components/forms/revision/fill-out/types';
import { IForm } from 'pages/revisions-module/template-editor/editors/form/types';
import { useCallback, useMemo } from 'react';
import { resolveDepartmentPlaceholderFields } from 'pages/revisions-module/template-editor/editors/template/extensions/placeholder/department-placeholders';

type RevisionLog = NonNullable<Revision['logs']>[number];

let draftBackgroundDataUrlPromise: Promise<string> | null = null;

/**
 * Resets the cached draft background between isolated test runs.
 */
export function resetDraftBackgroundDataUrlCacheForTests() {
  draftBackgroundDataUrlPromise = null;
}

/**
 * Loads the draft background asset once and converts it to a self-contained data URL.
 */
async function getDraftBackgroundDataUrl() {
  if (!draftBackgroundDataUrlPromise) {
    draftBackgroundDataUrlPromise = fetch('/img/pdf-background-draft.svg')
      .then(async (response) => {
        if (!response.ok) {
          throw new Error('Failed to load draft PDF background');
        }

        const svgSource = await response.text();
        return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgSource)}`;
      })
      .catch((error) => {
        draftBackgroundDataUrlPromise = null;
        throw error;
      });
  }

  return draftBackgroundDataUrlPromise;
}

/**
 * Integrates the document generator API into the application.
 */
export default function useDocumentGeneratorV2() {
  const { jwt } = useApi();

  const getLastLog = useCallback(
    (logs: RevisionLog[], oldStatusSlug: string, newStatusSlug: string): RevisionLog | undefined => {
      const [log] = logs
        .filter(({ oldStatus, newStatus }) => oldStatus?.slug === oldStatusSlug && newStatus?.slug === newStatusSlug)
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

      return log;
    },
    []
  );

  const preProcessTemplate = useCallback((template: string) => {
    const eTemplate = document.createElement('div');
    eTemplate.innerHTML = template;

    const deviceLoops = Array.from(eTemplate.getElementsByClassName('device-loop'));
    deviceLoops.forEach((deviceLoop) => {
      const start = deviceLoop.children.item(0)!;
      const body = deviceLoop.children.item(1)!;
      const end = deviceLoop.children.item(2)!;

      deviceLoop.replaceWith(start, ...body.children, end);
    });

    const ifBlocks = Array.from(eTemplate.getElementsByClassName('if-block'));
    ifBlocks.forEach((ifBlock) => {
      const start = ifBlock.children.item(0)!;
      const body = ifBlock.children.item(1)!;
      const end = ifBlock.children.item(2)!;

      ifBlock.replaceWith(start, ...body.children, end);
    });

    const alignBottomBlocks = Array.from(eTemplate.getElementsByClassName('align-bottom'));
    alignBottomBlocks.forEach((alignBottomBlock) => {
      alignBottomBlock.replaceWith(...alignBottomBlock.children);
    });

    return eTemplate.innerHTML;
  }, []);

  const preProcessVisual = useCallback(async (visual: any, isDraft: boolean) => {
    if (!isDraft) {
      return visual;
    }

    const draftBackgroundDataUrl = await getDraftBackgroundDataUrl();
    const customCss = typeof visual.customCss === 'string' ? visual.customCss : '';
    const draftBackgroundStyles = `
      .page-sheet {
        background-image: url("${draftBackgroundDataUrl}");
      }
    `;

    return {
      ...visual,
      customCss: `${customCss}\n${draftBackgroundStyles}`,
    };
  }, []);

  const preProcessData = useCallback(
    (data: RevisionFormData, { logs = [], revisionTemplate, department }: Revision) => {
      const approved = getLastLog(logs, 'finished-waiting-for-approval', 'approved-waiting-for-confirmation');

      const formScheme = revisionTemplate?.formScheme ? (JSON.parse(revisionTemplate.formScheme) as IForm) : null;
      const measurementSectionsById = Object.fromEntries(
        (formScheme?.sections ?? []).filter(({ tab }) => tab === 'measurements').map((s) => [s.id, s])
      );

      const devices = Object.values(data.devices)
        .filter(({ removed }) => !removed)
        .map((device) => ({
          ...device,
          measurements: Object.fromEntries(
            Object.entries(device.measurements).map(([sectionId, entries]) => {
              const measurementSection = measurementSectionsById[sectionId];

              return [
                sectionId,
                entries.map((entry, index) => ({
                  ...entry,
                  _faults: (entry._faults ?? []).map((fault) => ({
                    ...fault,
                    context: measurementSection
                      ? `Riadok č. ${index + 1} v tabuľke ${measurementSection.name}`
                      : fault.context,
                  })),
                })),
              ];
            })
          ),
        }));

      return {
        ...data,
        fields: {
          ...data.fields,
          ...resolveDepartmentPlaceholderFields(department?.breadcrumbs ?? [], data.fields),
          _approverName: approved?.user?.fullName ?? '',
          _approverOrganization: '', // TODO: Implement organization retrieval
          _approverPhone: '', // TODO: Implement phone retrieval
          _approverEmail: approved?.user?.email ?? '',
          _approverDate: approved ? new Date(approved.timestamp).toLocaleDateString('sk-SK') : '',
          _approverTime: approved ? new Date(approved.timestamp).toLocaleTimeString('sk-SK') : '',
        },
        devices,
      };
    },
    [getLastLog]
  );

  const callApi = useCallback(
    async ({
      endpoint,
      revision,
      overrideData,
      isDraft = false,
    }: {
      endpoint: 'preview' | 'generate';
      revision: Revision;
      overrideData?: any;
      isDraft?: boolean;
    }) => {
      const { template } = JSON.parse(revision.revisionTemplate!.templateScheme!);
      const visual = JSON.parse(revision.revisionTemplate!.visualData!);
      const revisionData = overrideData ?? JSON.parse(revision.revisionData!);
      const preparedVisual = await preProcessVisual(visual, isDraft);

      const response = await fetch(`/api/documents/v2/${endpoint}/`, {
        method: 'POST',
        body: JSON.stringify({
          data: preProcessData(revisionData, revision),
          template: preProcessTemplate(template),
          visual: preparedVisual,
        }),
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${jwt}`,
        },
      });

      return response;
    },
    [jwt, preProcessData, preProcessTemplate, preProcessVisual]
  );

  const generatePreview = useCallback(
    async (revision: Revision, overrideData?: any) => {
      const response = await callApi({ endpoint: 'preview', revision, overrideData });
      return await response.text();
    },
    [callApi]
  );

  const generatePdf = useCallback(
    async (revision: Revision, overrideData?: any) => {
      const response = await callApi({ endpoint: 'generate', revision, overrideData });
      return await response.blob();
    },
    [callApi]
  );

  const generateDraftPdf = useCallback(
    async (revision: Revision, overrideData?: any) => {
      const response = await callApi({ endpoint: 'generate', revision, overrideData, isDraft: true });
      return await response.blob();
    },
    [callApi]
  );

  const value = useMemo(
    () => ({ generatePreview, generatePdf, generateDraftPdf }),
    [generatePreview, generatePdf, generateDraftPdf]
  );

  return value;
}
